import type { FC } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Add, Remove } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Button, IconButton, Stack, TextField, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { toast } from '@/components/core/v2/toaster';
import { z as zod } from 'zod';

import type { Amenity, CreateAmenityRequest } from '@/types/v2/amenity';
import { createAmenity } from '@/api/amenities';
import { ProfessionalDialog } from '@/components/common';
import { useUser } from '@/hooks/v2/use-user';

const schema = zod.object({
  name: zod.string().min(1, 'Name is required'),
  description: zod.string().optional(),
  fee_description: zod.string().optional(),
  deposit_amount: zod.number().min(0).optional(),
  total_fee_amount: zod.number().min(0).optional(),
  rules: zod.string().optional(),
  required_documents: zod.array(zod.string()).optional().default([]),
});

type Values = zod.infer<typeof schema>;

interface CreateAmenityDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (amenity: Amenity) => void;
}

export const CreateAmenityDialog: FC<CreateAmenityDialogProps> = ({ open, onClose, onSuccess }) => {
  const { community } = useUser();
  const queryClient = useQueryClient();

  const defaultValues: Values = {
    name: '',
    description: '',
    fee_description: '',
    deposit_amount: undefined,
    total_fee_amount: undefined,
    rules: '',
    required_documents: [],
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    defaultValues,
    resolver: zodResolver(schema),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'required_documents',
  });

  const mutation = useMutation({
    mutationFn: (data: CreateAmenityRequest) => createAmenity(community?.id || '', data),
    onSuccess: (amenity) => {
      queryClient.invalidateQueries({ queryKey: ['amenities', community?.id] });
      toast.success('Amenity created successfully');
      reset();
      onSuccess(amenity);
      onClose();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to create amenity');
    },
  });

  const onSubmit = (data: Values) => {
    const required_documents = (data.required_documents ?? [])
      .map((s) => s.trim())
      .filter(Boolean);
    const requestData: CreateAmenityRequest = {
      ...data,
      community_id: community?.id || '',
      required_documents: required_documents.length > 0 ? required_documents : undefined,
    };
    mutation.mutate(requestData);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <ProfessionalDialog
      icon={<Add sx={{ fontSize: 24, color: 'primary.contrastText' }} />}
      maxWidth="sm"
      onClose={handleClose}
      open={open}
      subtitle={community?.name ? `Add an amenity for ${community.name}` : 'Add an amenity'}
      title="Add Amenity"
      actions={
        <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Button onClick={handleClose} variant="outlined">
              Cancel
            </Button>
            <LoadingButton disabled={isSubmitting} loading={isSubmitting} type="submit" variant="contained">
              {isSubmitting ? 'Creating...' : 'Create'}
            </LoadingButton>
          </Box>
        </form>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack mt={2} spacing={3}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Amenity Name"
                required
                fullWidth
                error={Boolean(errors.name)}
                helperText={errors.name?.message}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Description"
                fullWidth
                multiline
                rows={2}
                error={Boolean(errors.description)}
                helperText={errors.description?.message}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            )}
          />

          <Grid container spacing={2}>
            <Grid xs={12} md={6}>
              <Controller
                name="deposit_amount"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <TextField
                    {...field}
                    value={value ?? ''}
                    onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
                    label="Deposit Amount"
                    type="number"
                    fullWidth
                    error={Boolean(errors.deposit_amount)}
                    helperText={errors.deposit_amount?.message || 'Optional'}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
              />
            </Grid>
            <Grid xs={12} md={6}>
              <Controller
                name="total_fee_amount"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <TextField
                    {...field}
                    value={value ?? ''}
                    onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
                    label="Total Fee Amount"
                    type="number"
                    fullWidth
                    error={Boolean(errors.total_fee_amount)}
                    helperText={errors.total_fee_amount?.message || 'Optional'}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Controller
            name="fee_description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Fee Description"
                fullWidth
                error={Boolean(errors.fee_description)}
                helperText={errors.fee_description?.message}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            )}
          />

          <Controller
            name="rules"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Rules & Policies"
                fullWidth
                multiline
                rows={3}
                placeholder="Optional"
                error={Boolean(errors.rules)}
                helperText={errors.rules?.message}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            )}
          />

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Required documents (for deposit)
              </Typography>
              <Button
                type="button"
                size="small"
                startIcon={<Add />}
                onClick={() => append('')}
                sx={{ textTransform: 'none' }}
              >
                Add document
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Residents must upload each of these when paying the deposit.
            </Typography>
            <Stack spacing={1.5}>
              {fields.map((field, index) => (
                <Box key={field.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Controller
                    name={`required_documents.${index}`}
                    control={control}
                    render={({ field: f }) => (
                      <TextField
                        {...f}
                        fullWidth
                        placeholder="e.g. ID, Insurance, Signed agreement"
                        size="small"
                        error={Boolean(errors.required_documents?.[index])}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    )}
                  />
                  <IconButton
                    type="button"
                    size="small"
                    onClick={() => remove(index)}
                    aria-label="Remove document"
                    sx={{ mt: 0.5 }}
                  >
                    <Remove fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          </Box>
        </Stack>
      </form>
    </ProfessionalDialog>
  );
};
